import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Once an explicit login/verify sets the user, a late /auth/me 401 must not clobber it
  const authedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      authedRef.current = true;
      setUser(data.user);
    } catch {
      if (!authedRef.current) setUser(false);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("cc_token", data.token);
    authedRef.current = true;
    setUser(data.user);
    return data.user;
  }, []);

  const requestOtp = useCallback(async (phone) => (await api.post("/auth/driver/request-otp", { phone })).data, []);

  const verifyOtp = useCallback(async (phone, code) => {
    const { data } = await api.post("/auth/driver/verify-otp", { phone, code });
    if (data.token) localStorage.setItem("cc_token", data.token);
    authedRef.current = true;
    setUser(data.user);
    return data.user;
  }, []);

  const requestMagicLink = useCallback(async (email) => (await api.post("/auth/customer/request-link", { email })).data, []);

  const verifyMagicLink = useCallback(async (token) => {
    const { data } = await api.post("/auth/customer/verify-link", { token });
    if (data.token) localStorage.setItem("cc_token", data.token);
    authedRef.current = true;
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("cc_token");
    authedRef.current = false;
    setUser(false);
  }, []);

  return <AuthContext.Provider value={{ user, loading, login, requestOtp, verifyOtp, requestMagicLink, verifyMagicLink, logout, refresh }}>{children}</AuthContext.Provider>;
}
export const useAuth = () => useContext(AuthContext);
